import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPager, type Page } from '../crm/client/pager'

/** Most first pages kept for instant re-opens (each is one page of one list). */
const CACHE_MAX = 60
const firstPages = new Map<string, { items: unknown[]; hasMore: boolean }>()

function remember(key: string, items: unknown[], hasMore: boolean) {
  firstPages.delete(key)
  firstPages.set(key, { items, hasMore })
  while (firstPages.size > CACHE_MAX) firstPages.delete(firstPages.keys().next().value!)
}

/** Forget every remembered first page (tests). */
export function clearPagedListCache() {
  firstPages.clear()
}

/**
 * An OnlyFans list paged with createPager, as React state. A new `fetchPage`
 * (e.g. another category or filter) starts over from the first page; `reload`
 * re-reads the first page in place (the shown items stay until it lands).
 *
 * With a `cacheKey` (one per account + list + filter; null while searching),
 * the list's last first page shows at once on a re-open while OnlyFans is
 * asked again, and the fresh page replaces it when it lands.
 */
export function usePagedList<T>(
  fetchPage: (offset: number) => Promise<Page<T>>,
  idOf: (row: T) => string | number,
  cacheKey: string | null = null,
) {
  const [generation, setGeneration] = useState(0)
  // generation: reload() asks for a fresh pager over the same list.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const pager = useMemo(() => createPager({ fetchPage, idOf }), [fetchPage, idOf, generation])
  const quiet = useRef(false)
  const reloaded = useRef<(() => void)[]>([])
  const [items, setItems] = useState<T[]>([])
  const [hasMore, setHasMore] = useState(true)
  const [loading, setLoading] = useState(false)
  const [failed, setFailed] = useState(false)
  // Only the current pager may write state: a slow page from a previous
  // filter or category must not land on top of the new one.
  const current = useRef(pager)
  current.current = pager

  const loadMore = useCallback(async () => {
    if (!pager.hasMore) return
    setLoading(true)
    setFailed(false)
    let error = false
    try {
      await pager.loadMore()
    } catch {
      error = true
    }
    if (current.current !== pager) return
    setFailed(error)
    // A failed first page keeps what is shown (a remembered page, or the list being reloaded).
    if (!error || pager.items.length) setItems([...pager.items])
    setHasMore(pager.hasMore)
    setLoading(false)
  }, [pager])

  const key = useRef(cacheKey)
  key.current = cacheKey

  useEffect(() => {
    if (!quiet.current) {
      const cached = key.current ? firstPages.get(key.current) : undefined
      setItems(cached ? (cached.items as T[]) : [])
      setHasMore(cached ? cached.hasMore : true)
    }
    quiet.current = false
    const k = key.current
    const p = current.current
    void loadMore().then(() => {
      if (k && current.current === p && p.items.length) remember(k, [...p.items], p.hasMore)
      const waiting = reloaded.current
      reloaded.current = []
      for (const done of waiting) done()
    })
  }, [loadMore])

  /** Re-read from the first page without blanking the list; resolves when it landed. */
  const reload = useCallback(
    () =>
      new Promise<void>((resolve) => {
        quiet.current = true
        reloaded.current.push(resolve)
        setGeneration((g) => g + 1)
      }),
    [],
  )

  return { items, setItems, hasMore, loading, failed, loadMore, reload }
}
